import React from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

/**
 * AIDisclaimer component for displaying AI transparency and limitation notices
 * across dashboard overviews, assessment summaries, and analytics screens.
 * 
 * Props:
 * - variant: "card" (default) | "compact" | "banner"
 * - title: Custom header string
 * - text: Custom disclaimer body string
 * - sx: Additional MUI sx styling overrides
 */
export const AIDisclaimer = ({
  variant = "card",
  title = "AI Wellbeing Insights Disclaimer",
  text,
  sx = {},
}) => {
  const defaultText =
    "This summary is generated using artificial intelligence based on your assessment scores to help identify general wellness patterns. It is intended for self-reflection and personal growth, and does not constitute medical, psychological, or psychiatric advice.";

  const contentText = text || defaultText;

  if (variant === "compact") {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: "8px",
          backgroundColor: "#F3FBF9",
          border: "1px solid #E2ECE8",
          color: "#5C7268",
          fontSize: "12px",
          lineHeight: 1.4,
          ...sx,
        }}
      >
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: "#0E8B63", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "12px", color: "#5C7268" }}>
          {contentText}
        </Typography>
      </Box>
    );
  }

  if (variant === "banner") {
    return (
      <Box
        sx={{
          width: "100%",
          p: 1.5,
          borderRadius: "12px",
          backgroundColor: "#F3FBF9",
          border: "1px solid #E2ECE8",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          ...sx,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#E5F7F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: "#0E8B63",
            mt: 0.25,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              color: "#154C3A",
              mb: 0.25,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#5C7268",
              lineHeight: 1.5,
            }}
          >
            {contentText}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Default "card" variant
  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: "14px",
        borderColor: "rgba(226, 236, 232, 0.9)",
        backgroundColor: "#FAFDFB",
        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
        mt: 2,
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.25 }, "&:last-child": { pb: { xs: 2, sm: 2.25 } } }}>
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              backgroundColor: "#F3FBF9",
              border: "1px solid #E2ECE8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: "#0E8B63",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "13px",
                color: "#154C3A",
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#5C7268",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {contentText}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AIDisclaimer;
